import { z } from 'zod';
import { IsoDate, Uuid } from './envelope';
import { MetricValue } from './metric';
import { EntityRef } from './read-models';

/**
 * Release, environment and deployment read-models — the portfolio half only.
 *
 * The teardown scores twenty-one features across Planview Release and Planview
 * Verify. Fourteen of them are Not Applicable and deliberately so: release
 * orchestration, deployment plans, approval gating, blackout enforcement,
 * environment booking, conflict detection and drift control all sit inside
 * somebody's delivery pipeline, and this platform's entire position is that it
 * reads what a delivery tool produced rather than standing between a team and
 * their deployment. Building them would be a different product for a different
 * buyer.
 *
 * What is left is the portfolio question a release manager cannot answer from
 * the pipeline: **across the whole estate, what is landing when, is it landing
 * when it said it would, and what is concentrated in the window.** That is what
 * these shapes are.
 *
 * Three rules live in the types rather than in the screen.
 *
 * **A release with no date is listed, never placed.** `ReleaseRow.bucket` is
 * nullable and `undatedReason` says why. Twenty-seven releases in the estate
 * are the product team's next release with no committed date; putting them at
 * a plausible point would invent a commitment nobody made.
 *
 * **A release with no measurement is not a release that failed.** `status`,
 * `scopeItemCount` and `defectCount` are nullable and carry
 * `measurementReason` when absent. Twenty-seven releases have no row in the
 * delivery measures at all, and a calendar that drew them as zero-defect would
 * be reporting a quality record nobody kept.
 *
 * **Slip is only computed where both dates exist.** `slipDays` is null for
 * anything not yet landed. A release that has not shipped is not a release that
 * shipped on time, and the on-time share below counts only what has landed and
 * says how many it excluded.
 */

// ─────────────────────────────────────────────────────────────────────────────
// 1. The calendar — /v2/releases
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Where a release had got to, as the delivery measures record it.
 *
 * Not an enum the platform invented: these are the values already in
 * `fact.release_metric.status_code`. `null` is a real state and means no
 * measurement row exists, which is different from every value here.
 */
export const ReleaseStatus = z.enum([
  'Planned',
  'InTest',
  'Deploying',
  'InFlight',
  'Released',
  'RolledBack',
  'Cancelled',
]);
export type ReleaseStatus = z.infer<typeof ReleaseStatus>;

export const ReleaseGrouping = z.enum(['valueStream', 'productGroup', 'product']);
export type ReleaseGrouping = z.infer<typeof ReleaseGrouping>;

/**
 * An outcome this release was supposed to move.
 *
 * Nullable on every row in this build, and the reason is on
 * `ReleaseCalendarView.outcomeLinkage` rather than repeated here. The claim
 * records exist — sixteen of them, each written by a named person — but they
 * name releases by a code string that is not the register's key and is not
 * unique within itself, so none of them resolves to a release. The type is here
 * because the linkage is the right shape and the join is the thing that has to
 * be fixed; filling it from a code that collides would be inventing the very
 * traceability this screen exists to prove.
 */
export const ReleaseOutcomeClaim = z.object({
  outcomeId: Uuid,
  outcomeName: z.string(),
  /** How the link came to exist. A signed claim and an inferred one differ. */
  linkType: z.enum(['DeclaredByTeam', 'InferredFromReference', 'InferredFromTiming']),
  verdictState: z.enum(['Measured', 'Consistent', 'ClaimOnly', 'NoMovement', 'Refused']),
  verdictHeadline: z.string(),
  route: z.string().nullable(),
});
export type ReleaseOutcomeClaim = z.infer<typeof ReleaseOutcomeClaim>;

export const ReleaseRow = z.object({
  id: Uuid,
  businessCode: z.string().nullable(),
  name: z.string(),
  product: EntityRef.nullable(),
  /** The group the row is filed under, per the requested grouping. */
  groupId: z.string(),
  groupLabel: z.string(),

  plannedDate: IsoDate.nullable(),
  actualDate: IsoDate.nullable(),
  /**
   * The month this release is filed under — the actual date where it has
   * landed, the planned date where it has not. Null for an undated release.
   */
  bucket: z.string().regex(/^\d{4}-\d{2}$/).nullable(),
  undatedReason: z.string().nullable(),

  /** Actual minus planned, in days. Null unless the release has both. */
  slipDays: z.number().int().nullable(),

  status: ReleaseStatus.nullable(),
  scopeItemCount: z.number().int().nonnegative().nullable(),
  defectCount: z.number().int().nonnegative().nullable(),
  isRolledBack: z.boolean().nullable(),
  /** Why the four fields above are null, where they are. */
  measurementReason: z.string().nullable(),

  outcomeClaim: ReleaseOutcomeClaim.nullable(),
  route: z.string().nullable(),
});
export type ReleaseRow = z.infer<typeof ReleaseRow>;

/** One month of the calendar. Every month in the window appears, including the empty ones. */
export const ReleaseMonth = z.object({
  month: z.string().regex(/^\d{4}-\d{2}$/),
  label: z.string(),
  /** Releases filed under this month, whether they have landed or not. */
  releaseCount: z.number().int().nonnegative(),
  landedCount: z.number().int().nonnegative(),
  plannedCount: z.number().int().nonnegative(),
  /** Landed releases whose actual date was later than their planned date. */
  lateCount: z.number().int().nonnegative(),
});
export type ReleaseMonth = z.infer<typeof ReleaseMonth>;

/**
 * One cell of the calendar: a group in a month.
 *
 * Counted in SQL over every release in the window rather than derived from
 * `ReleaseCalendarView.rows`, which is a capped page. Ordered by date, that cap
 * runs out partway through the window and takes the last months of the plan
 * with it — so a calendar built from the rows showed a portfolio that stops
 * shipping in August, which is a truncation drawn as a fact.
 */
export const ReleaseCell = z.object({
  groupId: z.string(),
  month: z.string().regex(/^\d{4}-\d{2}$/),
  releaseCount: z.number().int().positive(),
  landedCount: z.number().int().nonnegative(),
  lateCount: z.number().int().nonnegative(),
});
export type ReleaseCell = z.infer<typeof ReleaseCell>;

export const ReleaseGroupSummary = z.object({
  id: z.string(),
  label: z.string(),
  entityType: z.string().nullable(),
  route: z.string().nullable(),
  releaseCount: z.number().int().nonnegative(),
  undatedCount: z.number().int().nonnegative(),
});
export type ReleaseGroupSummary = z.infer<typeof ReleaseGroupSummary>;

/**
 * Deployment activity by month, over the same window the calendar draws.
 *
 * Deployments are recorded against the product, never against the release —
 * `fact.deployment.release_entity_id` is null on all 2,875 rows — so this is a
 * portfolio trend beside the calendar rather than a figure per release, and
 * `attribution` says so on the view.
 */
export const ReleaseTrendPoint = z.object({
  month: z.string().regex(/^\d{4}-\d{2}$/),
  label: z.string(),
  deployments: z.number().int().nonnegative(),
  failedDeployments: z.number().int().nonnegative(),
  /** Failed / total for the month. Null where the month has no deployments. */
  changeFailureRate: z.number().min(0).max(1).nullable(),
  /** Releases whose actual date falls in this month. */
  releasesLanded: z.number().int().nonnegative(),
  /** Set where the month recorded nothing, so an empty point is not read as zero activity. */
  unavailableReason: z.string().nullable(),
});
export type ReleaseTrendPoint = z.infer<typeof ReleaseTrendPoint>;

/**
 * What is concentrated on the products releasing into this window.
 *
 * The join is release → product → open risk, because a risk is raised against
 * a product and nothing in the estate raises one against a release. One row per
 * product that has a release in the window and at least one open risk.
 */
export const ReleaseRiskConcentration = z.object({
  product: EntityRef,
  releaseCount: z.number().int().positive(),
  openRiskCount: z.number().int().positive(),
  highestSeverityBand: z.enum(['Critical', 'High', 'Moderate', 'Low']),
  /** The single risk driving the band, so the row leads somewhere. */
  topRiskTitle: z.string(),
  topRiskId: Uuid,
});
export type ReleaseRiskConcentration = z.infer<typeof ReleaseRiskConcentration>;

export const ReleaseCalendarView = z.object({
  groupBy: ReleaseGrouping,
  windowStart: IsoDate,
  windowEnd: IsoDate,

  months: z.array(ReleaseMonth),
  groups: z.array(ReleaseGroupSummary),
  /** The grid itself. Complete for the window, whatever `rows` was capped at. */
  cells: z.array(ReleaseCell),
  /**
   * The releases behind the grid, as a page.
   *
   * Capped, and `totals.notListed` says by how much. These carry the detail a
   * cell cannot — name, code, dates, slip, what was measured — and feed the
   * export; no count on this screen is derived from them.
   */
  rows: z.array(ReleaseRow),
  /** Releases with no date at all, listed rather than dropped. */
  undated: z.array(ReleaseRow),

  trend: z.array(ReleaseTrendPoint),
  riskConcentration: z.array(ReleaseRiskConcentration),

  metrics: z.array(MetricValue),

  /**
   * The state of release-to-outcome traceability, stated rather than implied by
   * an empty column.
   */
  outcomeLinkage: z.object({
    releasesInView: z.number().int().nonnegative(),
    releasesWithClaim: z.number().int().nonnegative(),
    /** Claims that name a release the register does not hold. */
    unresolvedClaims: z.number().int().nonnegative(),
    reason: z.string().nullable(),
  }),

  /** How deployments are attached, said once rather than assumed. */
  deploymentAttribution: z.string(),

  totals: z.object({
    placed: z.number().int().nonnegative(),
    undated: z.number().int().nonnegative(),
    landed: z.number().int().nonnegative(),
    /** Releases in the window the row list was capped before reaching. */
    notListed: z.number().int().nonnegative(),
  }),
});
export type ReleaseCalendarView = z.infer<typeof ReleaseCalendarView>;
